import { useState, useEffect, useRef, useCallback } from 'react'
import Select from 'react-select'
import { useAuth } from '../contexts/AuthContext'
import { feedbackAPI } from '../services/api'
import 'bootstrap-icons/font/bootstrap-icons.css'

const STATUS_LABELS = {
  under_review: 'Under Review',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
}

const STATUS_STYLES = {
  under_review: 'bg-amber-100 text-amber-800 border-amber-300',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
  resolved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  closed: 'bg-gray-200 text-gray-700 border-gray-300',
}

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_STYLES[status] || STATUS_STYLES.under_review}`}>
    {STATUS_LABELS[status] || status}
  </span>
)

const MessageBubble = ({ msg }) => (
  <div className={`flex ${msg.is_developer_reply ? 'justify-start' : 'justify-end'} mb-3`}>
    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm ${
      msg.is_developer_reply
        ? 'bg-gray-100 text-gray-900 rounded-bl-sm'
        : 'bg-gradient-to-r from-kbl-neon-500 to-kbl-neon-600 text-black rounded-br-sm'
    }`}>
      <div className="text-xs font-semibold opacity-70 mb-0.5">
        {msg.author_welcome_name || msg.author_username}
      </div>
      <div className="whitespace-pre-wrap break-words text-sm">{msg.body}</div>
      <div className="text-[10px] opacity-60 mt-1 text-right">
        {new Date(msg.created_at).toLocaleString()}
      </div>
    </div>
  </div>
)

const initials = (name) => (name || '?').trim().slice(0, 2).toUpperCase()

const relativeTime = (iso) => {
  const diffMs = Date.now() - new Date(iso).getTime()
  const mins = Math.round(diffMs / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

const ToDeveloper = () => {
  const { isSuperuser } = useAuth()
  return isSuperuser ? <DeveloperInbox /> : <UserChat />
}

// ---------- Regular / staff user view: single chat with the developer team ----------
const UserChat = () => {
  const [thread, setThread] = useState(null)
  const [loading, setLoading] = useState(true)
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const messagesEndRef = useRef(null)

  const loadThread = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const res = await feedbackAPI.getMyThread()
      setThread(res.data.data)
      setError(null)
    } catch (err) {
      setError('Failed to load your conversation.')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadThread()
    const interval = setInterval(() => loadThread(true), 8000)
    return () => clearInterval(interval)
  }, [loadThread])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [thread?.messages?.length])

  const isClosed = thread && (thread.status === 'resolved' || thread.status === 'closed')

  const handleSend = async (e) => {
    e.preventDefault()
    const body = newMessage.trim()
    if (!body || sending) return
    setSending(true)
    try {
      let activeThread = thread
      if (!activeThread) {
        const res = await feedbackAPI.startNewThread()
        activeThread = res.data.data
      }
      await feedbackAPI.postMessage(activeThread.id, body)
      setNewMessage('')
      await loadThread(true)
    } catch (err) {
      setError('Failed to send message.')
    } finally {
      setSending(false)
    }
  }

  const handleStartNew = async () => {
    try {
      await feedbackAPI.startNewThread()
      await loadThread()
    } catch (err) {
      setError('Failed to start a new conversation.')
    }
  }

  return (
    <div className="page-container">
      <div className="section-header">
        <i className="bi bi-chat-dots"></i>
        To Developer
      </div>

      <div className="bg-white rounded-xl shadow-medium border border-gray-200 flex flex-col" style={{ height: '65vh' }}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-kbl-neon-700">Talk to the Developer</h2>
          {thread && <StatusBadge status={thread.status} />}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center text-gray-500">Loading...</div>
          ) : error && !thread ? (
            <div className="text-center text-red-600">{error}</div>
          ) : thread?.messages?.length ? (
            <>
              {thread.messages.map((msg) => (
                <MessageBubble key={msg.id} msg={msg} />
              ))}
              <div ref={messagesEndRef} />
            </>
          ) : (
            <div className="text-center text-gray-400 mt-10">
              <i className="bi bi-chat-square-text text-4xl mb-2 block"></i>
              Send a message to start the conversation.
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200">
          {isClosed ? (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                This conversation is {STATUS_LABELS[thread.status].toLowerCase()}.
              </p>
              <button
                onClick={handleStartNew}
                className="px-5 py-2.5 font-semibold rounded-xl bg-gradient-to-r from-kbl-neon-500 to-kbl-neon-600 text-black shadow-glow-neon"
              >
                Start New Conversation
              </button>
            </div>
          ) : (
            <form onSubmit={handleSend} className="flex gap-3">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2.5 border rounded-xl"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className="px-5 py-2.5 font-semibold rounded-xl bg-gradient-to-r from-kbl-neon-500 to-kbl-neon-600 text-black shadow-glow-neon disabled:opacity-50"
              >
                <i className="bi bi-send"></i>
              </button>
            </form>
          )}
          {error && thread && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </div>
      </div>
    </div>
  )
}

const FILTER_TABS = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'closed', label: 'Closed' },
]

const isOpenStatus = (status) => status === 'under_review' || status === 'in_progress'

// ---------- Superuser view: Gmail/Front-style master-detail inbox ----------
const DeveloperInbox = () => {
  const [threads, setThreads] = useState([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [needsReplyOnly, setNeedsReplyOnly] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [selectedThread, setSelectedThread] = useState(null)
  const [loadingList, setLoadingList] = useState(true)
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [showNewMessageModal, setShowNewMessageModal] = useState(false)
  const [userOptions, setUserOptions] = useState([])
  const [newRecipient, setNewRecipient] = useState(null)
  const [newInitialMessage, setNewInitialMessage] = useState('')
  const [startingThread, setStartingThread] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const loadInbox = useCallback(async (silent = false) => {
    if (!silent) setLoadingList(true)
    try {
      const res = await feedbackAPI.getInbox()
      setThreads(res.data.data || [])
    } catch (err) {
      setError('Failed to load inbox.')
    } finally {
      if (!silent) setLoadingList(false)
    }
  }, [])

  const loadThreadDetail = useCallback(async (id, silent = false) => {
    try {
      const res = await feedbackAPI.getThreadDetail(id)
      setSelectedThread(res.data.data)
    } catch (err) {
      if (!silent) setError('Failed to load conversation.')
    }
  }, [])

  useEffect(() => {
    loadInbox()
    const interval = setInterval(() => loadInbox(true), 10000)
    return () => clearInterval(interval)
  }, [loadInbox])

  useEffect(() => {
    if (selectedId) {
      loadThreadDetail(selectedId)
      inputRef.current?.focus()
      const interval = setInterval(() => loadThreadDetail(selectedId, true), 8000)
      return () => clearInterval(interval)
    }
  }, [selectedId, loadThreadDetail])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [selectedThread?.messages?.length])

  const handleSelect = (id) => {
    setSelectedId(id)
    setSelectedThread(null)
    setError(null)
  }

  const handleBack = () => {
    setSelectedId(null)
    setSelectedThread(null)
  }

  const handleSend = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || !selectedId) return
    setSending(true)
    try {
      await feedbackAPI.postMessage(selectedId, newMessage.trim())
      setNewMessage('')
      await loadThreadDetail(selectedId, true)
      await loadInbox(true)
    } catch (err) {
      setError('Failed to send reply.')
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  const handleStatusChange = async (newStatus) => {
    if (!selectedId) return
    try {
      await feedbackAPI.updateStatus(selectedId, newStatus)
      await loadThreadDetail(selectedId, true)
      await loadInbox(true)
    } catch (err) {
      setError('Failed to update status.')
    }
  }

  const handleDeleteThread = async (id, username, e) => {
    e?.stopPropagation()
    if (!window.confirm(`Permanently delete the conversation with ${username}? This cannot be undone.`)) return
    try {
      await feedbackAPI.deleteThread(id)
      if (selectedId === id) {
        setSelectedId(null)
        setSelectedThread(null)
      }
      await loadInbox(true)
    } catch (err) {
      setError('Failed to delete conversation.')
    }
  }

  const openNewMessageModal = async () => {
    setShowNewMessageModal(true)
    setNewRecipient(null)
    setNewInitialMessage('')
    if (userOptions.length === 0) {
      try {
        const res = await feedbackAPI.getUserOptions()
        setUserOptions((res.data.data || []).map((u) => ({ value: u.id, label: u.welcome_name || u.username })))
      } catch (err) {
        console.error('Failed to load users', err)
      }
    }
  }

  const handleStartThreadForUser = async (e) => {
    e.preventDefault()
    if (!newRecipient || !newInitialMessage.trim()) return
    setStartingThread(true)
    try {
      const res = await feedbackAPI.startThreadForUser(newRecipient.value, newInitialMessage.trim())
      setShowNewMessageModal(false)
      await loadInbox(true)
      setSelectedId(res.data.data.id)
      setSelectedThread(res.data.data)
    } catch (err) {
      setError('Failed to start conversation.')
    } finally {
      setStartingThread(false)
    }
  }

  const needsReplyCount = threads.filter((t) => t.needs_reply).length

  const filteredThreads = threads.filter((t) => {
    if (statusFilter === 'open' && !isOpenStatus(t.status)) return false
    if (statusFilter === 'resolved' && t.status !== 'resolved') return false
    if (statusFilter === 'closed' && t.status !== 'closed') return false
    if (needsReplyOnly && !t.needs_reply) return false
    if (search.trim() && !t.username.toLowerCase().includes(search.trim().toLowerCase())) return false
    return true
  })

  return (
    <div className="page-container">
      <div className="section-header flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <i className="bi bi-inbox"></i>
          KBL Inbox
          {needsReplyCount > 0 && (
            <span className="text-xs font-semibold bg-red-500 text-white rounded-full px-2.5 py-1">
              {needsReplyCount} needs reply
            </span>
          )}
        </div>
        <button
          onClick={openNewMessageModal}
          className="px-5 py-2.5 font-semibold rounded-xl bg-gradient-to-r from-kbl-neon-500 to-kbl-neon-600 text-black shadow-glow-neon flex items-center gap-2 text-sm"
        >
          <i className="bi bi-pencil-square"></i>
          New Message
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-medium border border-gray-200 flex overflow-hidden" style={{ height: '75vh' }}>
        {/* List pane */}
        <div className={`w-full sm:w-[360px] border-r border-gray-200 flex-col flex-shrink-0 ${selectedId ? 'hidden sm:flex' : 'flex'}`}>
          <div className="p-3 border-b border-gray-200 space-y-2 flex-shrink-0">
            <div className="relative">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by username..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-kbl-neon-500/50 focus:border-kbl-neon-500"
              />
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto">
              {FILTER_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                    statusFilter === tab.id
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              <button
                onClick={() => setNeedsReplyOnly((v) => !v)}
                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold transition-colors flex items-center gap-1 ${
                  needsReplyOnly
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <i className="bi bi-exclamation-circle-fill text-[10px]"></i>
                Needs reply
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingList ? (
              <div className="p-3 space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse"></div>
                ))}
              </div>
            ) : filteredThreads.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                <i className="bi bi-inbox text-3xl mb-2 block text-gray-300"></i>
                No conversations match.
              </div>
            ) : (
              filteredThreads.map((t) => (
                <div
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelect(t.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSelect(t.id) }}
                  className={`group w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors flex items-center gap-3 cursor-pointer ${
                    selectedId === t.id ? 'bg-kbl-neon-50 border-l-4 border-l-kbl-neon-500 pl-3' : ''
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-kbl-neon-500/20 to-kbl-neon-600/20 border-2 border-kbl-neon-500/40 flex items-center justify-center text-xs font-semibold text-kbl-neon-700">
                      {initials(t.username)}
                    </div>
                    {t.needs_reply && (
                      <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 border-2 border-white rounded-full"></span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm truncate ${t.needs_reply ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                        {t.username}
                      </span>
                      <span className="text-[11px] text-gray-400 flex-shrink-0">{relativeTime(t.updated_at)}</span>
                    </div>
                    <p className={`text-xs truncate ${t.needs_reply ? 'text-gray-700' : 'text-gray-500'}`}>
                      {t.last_message_preview || 'No messages yet'}
                    </p>
                    <div className="mt-1 flex items-center gap-1.5">
                      <StatusBadge status={t.status} />
                      {!t.is_active && <span className="text-[10px] text-gray-400 uppercase tracking-wide">archived</span>}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeleteThread(t.id, t.username, e)}
                    title="Delete conversation"
                    className="flex flex-shrink-0 w-7 h-7 items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <i className="bi bi-trash text-sm"></i>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Detail pane */}
        <div className={`flex-1 flex-col ${selectedId ? 'flex' : 'hidden sm:flex'}`}>
          {!selectedId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <i className="bi bi-chat-square-text text-4xl mb-3 text-gray-300"></i>
              <p className="text-sm">Select a conversation to view it here.</p>
            </div>
          ) : (
            <>
              <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between flex-shrink-0 bg-white">
                <div className="flex items-center gap-3 min-w-0">
                  <button onClick={handleBack} className="sm:hidden text-gray-500 hover:text-gray-700 flex-shrink-0">
                    <i className="bi bi-arrow-left text-lg"></i>
                  </button>
                  <div className="w-9 h-9 flex-shrink-0 rounded-full bg-gradient-to-br from-kbl-neon-500/20 to-kbl-neon-600/20 border-2 border-kbl-neon-500/40 flex items-center justify-center text-xs font-semibold text-kbl-neon-700">
                    {selectedThread ? initials(selectedThread.username) : ''}
                  </div>
                  <h3 className="font-semibold text-gray-900 truncate">{selectedThread?.username || 'Loading...'}</h3>
                </div>
                {selectedThread && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <select
                      value={selectedThread.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium"
                    >
                      {Object.entries(STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleDeleteThread(selectedId, selectedThread.username)}
                      title="Delete conversation"
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 bg-gray-50">
                {!selectedThread ? (
                  <div className="space-y-3 animate-pulse">
                    <div className="flex justify-start"><div className="w-2/3 h-10 bg-gray-200 rounded-2xl"></div></div>
                    <div className="flex justify-end"><div className="w-1/2 h-10 bg-gray-200 rounded-2xl"></div></div>
                  </div>
                ) : selectedThread.messages?.length ? (
                  <>
                    {selectedThread.messages.map((msg) => (
                      <MessageBubble key={msg.id} msg={msg} />
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                ) : (
                  <div className="text-center text-gray-400 mt-10">No messages yet.</div>
                )}
              </div>

              <div className="px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0">
                <form onSubmit={handleSend} className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSend(e)
                      }
                    }}
                    placeholder="Reply as the Developer..."
                    rows={1}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-kbl-neon-500/50 focus:border-kbl-neon-500 max-h-28"
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-xl bg-gradient-to-r from-kbl-neon-500 to-kbl-neon-600 text-black shadow-glow-neon disabled:opacity-50"
                  >
                    <i className={`bi ${sending ? 'bi-arrow-repeat animate-spin' : 'bi-send'}`}></i>
                  </button>
                </form>
                {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
              </div>
            </>
          )}
        </div>
      </div>

      {showNewMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800">New Message</h3>
              <button
                onClick={() => setShowNewMessageModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="bi bi-x-lg text-xl"></i>
              </button>
            </div>
            <form onSubmit={handleStartThreadForUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">To</label>
                <Select
                  options={userOptions}
                  value={newRecipient}
                  onChange={setNewRecipient}
                  placeholder="Select a user..."
                  classNamePrefix="react-select"
                  isClearable
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Message</label>
                <textarea
                  value={newInitialMessage}
                  onChange={(e) => setNewInitialMessage(e.target.value)}
                  placeholder="Write your message..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-kbl-neon-500/50 focus:border-kbl-neon-500"
                />
              </div>
              <p className="text-xs text-gray-500">
                If this user already has an open conversation, your message will be added to it instead of starting a duplicate.
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewMessageModal(false)}
                  className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={startingThread || !newRecipient || !newInitialMessage.trim()}
                  className="px-5 py-2 font-semibold rounded-lg bg-gradient-to-r from-kbl-neon-500 to-kbl-neon-600 text-black disabled:opacity-50"
                >
                  {startingThread ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default ToDeveloper
